// src/composables/useBugCapture.js
//
// Orchestrates auto-capture for the quick bug button: screenshot the current
// viewport with html2canvas, upload it to Firebase Storage, fetch the last N
// eventLog entries for the current user, snapshot the in-memory console ring
// buffer, and hand the whole bundle to `submitBugReport`.
//
// The caller (QuickBugModal) supplies only the title + description — everything
// else is captured automatically.

import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { storage, db, functions } from '@/services/firebaseconfig'
import { useUserStore } from '@/stores/userStore'
import { snapshotConsole } from '@/composables/useConsoleCapture'
import { isGooglePresentationCaptureElement } from '@/utils/designerPresentationPolicy'

const RECENT_EVENTS_LIMIT = 30

/**
 * Grab a PNG blob of the current viewport. Uses html2canvas. We dynamic-import
 * it so the library doesn't land in the main bundle for users who never click
 * the bug button.
 */
async function captureScreenshot() {
  try {
    const html2canvas = (await import('html2canvas')).default
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: document.documentElement.scrollHeight,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      ignoreElements: el =>
        el?.dataset?.bugCaptureHide === 'true' || isGooglePresentationCaptureElement(el),
    })
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 0.92))
    return blob
  } catch (err) {
    console.warn('[useBugCapture] Screenshot failed:', err?.message)
    return null
  }
}

async function uploadScreenshotBlob(blob, uid) {
  if (!blob || !uid) return null
  const path = `bug_reports/${uid}/quick/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.png`
  const fileRef = storageRef(storage, path)
  try {
    await uploadBytes(fileRef, blob, { contentType: 'image/png' })
    const url = await getDownloadURL(fileRef)
    return {
      name: 'auto-capture.png',
      url,
      storagePath: path,
      contentType: 'image/png',
      size: blob.size,
    }
  } catch (err) {
    console.warn('[useBugCapture] Screenshot upload failed:', err?.message)
    return null
  }
}

/**
 * Last N events for the calling user. eventLog allows list for any signed-in
 * user (firestore.rules §eventLog); we scope by actor.uid so a non-admin
 * submitter only sees their own actions.
 */
async function fetchRecentEvents(uid) {
  if (!uid) return []
  try {
    const q = query(
      collection(db, 'eventLog'),
      where('actor.uid', '==', uid),
      orderBy('timestamp', 'desc'),
      limit(RECENT_EVENTS_LIMIT)
    )
    const snap = await getDocs(q)
    return snap.docs.map(d => {
      const data = d.data() || {}
      const ts = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : null
      return {
        id: d.id,
        eventType: data.eventType || null,
        action: data.action || null,
        description: data.description || null,
        resource: data.resource || null,
        timestamp: ts,
      }
    })
  } catch (err) {
    console.warn('[useBugCapture] Recent-events query failed:', err?.message)
    return []
  }
}

function collectPageContext() {
  const ctx = {
    pageUrl: '',
    userAgent: '',
    viewport: { width: 0, height: 0 },
    documentTitle: '',
    referrer: '',
  }
  if (typeof window === 'undefined') return ctx
  ctx.pageUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  ctx.userAgent = navigator.userAgent || ''
  ctx.viewport = { width: window.innerWidth, height: window.innerHeight }
  ctx.documentTitle = document.title || ''
  ctx.referrer = document.referrer || ''
  return ctx
}

/**
 * Run the full capture pipeline and submit a bug report. Returns the new
 * bugReportId. Quick capture is bug-only — feature requests are handled
 * through the full submit modal on the Bug Reports page, where the "why"
 * field matters more than the auto-captured context.
 *
 * @param {Object} params
 * @param {string} params.title
 * @param {string} params.description
 * @param {'critical'|'high'|'medium'|'low'} [params.severity='medium']
 * @returns {Promise<{ bugReportId: string, type: string }>}
 */
export async function captureAndSubmitQuickBug({
  title,
  description,
  severity = 'medium',
  watcherUids = [],
}) {
  const userStore = useUserStore()
  const uid = userStore.user?.uid
  if (!uid) throw new Error('Sign in required to submit a bug.')

  const context = collectPageContext()

  // Kick off screenshot + events in parallel.
  const [screenshotBlob, recentEvents] = await Promise.all([
    captureScreenshot(),
    fetchRecentEvents(uid),
  ])
  const consoleMessages = snapshotConsole()

  const uploadedScreenshot = await uploadScreenshotBlob(screenshotBlob, uid)
  const screenshots = uploadedScreenshot ? [uploadedScreenshot] : []

  const submitFn = httpsCallable(functions, 'submitBugReport')
  const payload = {
    watcherUids,
    type: 'bug',
    severity,
    title: title?.trim() || 'Quick bug report',
    description: description?.trim() || '(no description provided)',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    isRepeatable: null, // Quick capture doesn't ask — don't default to "No" and mislead the admin.
    consoleLogs: '',
    pageUrl: context.pageUrl,
    userAgent: context.userAgent,
    screenshots,
    capture: {
      type: 'quick',
      capturedAt: new Date().toISOString(),
      documentTitle: context.documentTitle,
      referrer: context.referrer,
      viewport: context.viewport,
      consoleMessages,
      recentEvents,
    },
  }

  const res = await submitFn(payload)
  return res?.data || {}
}
